import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import getTheme from '../../theme/theme';
import { ThemeProvider } from '@mui/material';
import { ARROW, BASEURL } from '../../constants/Common';
import FloatingButton from '../usersupport/helpdesk/FloatingButton';

interface SubscriptionProps {
  title: string;
  price: number;
  currency: string;
  users: number;
  projects: number;
  features: { name: string }[];
}

const SubscriptionPlan = (): JSX.Element => {
  const theme = getTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // user as admin store the id
  const storedAdmindata = localStorage.getItem('userRoleAdmin');
  let UserroleAdmin: any = null;
  if (storedAdmindata) {
    UserroleAdmin = JSON.parse(storedAdmindata);
  } else {
    console.log('No data found in localStorage');
  }
  const [userRole, setUserRole] = useState<string>(UserroleAdmin);
  const [subscriptions, setSubscriptions] = useState<SubscriptionProps[]>([]);
  const [loading, setLoading] = useState(true);
  const orgfin = location?.state?.data;
  const hasUsedFreetrial = orgfin?.organization?.has_used_freetrial;

  // Fetch pricing details from the pricing table
  const fetchSubscriptions = () => {
    axios
      .get<SubscriptionProps[]>(BASEURL + '/pricing', {
        headers: {
          Accept: 'application/json',
        },
      })
      .then((response) => {
        setSubscriptions(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handlePurchase = (selectedSubscription: SubscriptionProps) => {
    // Navigate to the purchase page with selected subscription details
    navigate('/purchase', {
      state: { subscriptionData: selectedSubscription, orgfinData: orgfin },
    });
  };

  const handleFreeTrialClick = () => {
    // Find the "Premium - Free trial" subscription
    const freeTrialSubscription = subscriptions.find(
      (subscription) => subscription.title === 'Premium Plus - Free trial'
    );

    if (freeTrialSubscription) {
      navigate('/subscriptions-freetrial', {
        state: { subscriptionData: freeTrialSubscription, orgfinData: orgfin },
      });
    } else {
      console.log('Premium Plus - Free trial plan not found');
    }
  };

  const isUserAdmin = userRole === 'Admin';

  return (
    <ThemeProvider theme={theme}>
      <div id="subscription">
        <Box
          sx={{
            pt: 5,
            pb: 10,
            px: 2,
            mt: '-80px',
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              <Link to="/subscriptions" style={{ padding: '15px', display: 'inline-block' }}>
                Subscriptions
              </Link> {ARROW} View Plans
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: '50px' }}>
              <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={loading}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
            </Box>
          ) : (
            <Container>
              <Grid container spacing={4}>
                {subscriptions
                  .filter((subscription) => subscription.title !== 'Premium Plus - Free trial')
                  .map((subscription, i) => (
                    <Grid item xs={12} md={4} key={i}>
                      <Card
                        component={Box}
                        height={1}
                        display="flex"
                        flexDirection="column"
                        boxShadow={0}
                        sx={{
                          backgroundColor: theme.palette.background.default,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <CardContent
                          sx={{
                            padding: { sm: 4 },
                          }}
                        >
                          <Box
                            marginBottom={4}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: theme.palette.text.primary,
                              }}
                              gutterBottom
                            >
                              <Box component="span" fontWeight={600}>
                                {subscription.title}
                              </Box>
                            </Typography>
                            <Box display="flex" alignItems="flex-start">
                              <Typography variant="h4" color="#9FCF3A">
                                <Box
                                  component="span"
                                  fontWeight={600}
                                  marginRight={1 / 2}
                                >
                                  {subscription.currency}
                                </Box>
                              </Typography>
                              <Typography
                                variant="h2"
                                color="#9FCF3A"
                                gutterBottom
                              >
                                <Box component="span" fontWeight={600}>
                                  {subscription.price}
                                </Box>
                                <Typography variant="h6" component="span" sx={{ marginLeft: 0.5 }}>
                                  /year
                                </Typography>
                              </Typography>
                            </Box>
                            <Box>
                              <Typography color={theme.palette.text.primary}>
                                Number of users: {subscription.users}
                                <br />
                                Number of projects: {subscription.projects}
                              </Typography>
                            </Box>
                          </Box>
                          <Grid container spacing={1}>
                            {subscription.features?.map((feature, j) => (
                              <Grid item xs={12} key={j}>
                                <Typography
                                  component="p"
                                  align="center"
                                  sx={{
                                    textDecoration: 'none',
                                  }}
                                >
                                  {feature.name}
                                </Typography>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                        <Box flexGrow={1} />
                        <CardActions sx={{ justifyContent: 'center', padding: 4, flexDirection: 'column' }}>
                          {subscription.title === 'Premium Plus' && !hasUsedFreetrial && (
                            <Button
                              size="large"
                              variant="contained"
                              endIcon={<ArrowForwardIcon />}
                              onClick={handleFreeTrialClick}
                              disabled={!isUserAdmin}
                              sx={{
                                textTransform: 'uppercase',
                                color: theme.palette.common.black,
                                mb: 2,
                                '&:disabled': { // Adding styles for disabled state
                                  color: theme.palette.grey[500], // Set text color to gray
                                }
                              }}
                            >
                              Free trial
                            </Button>
                          )}
                          <Button
                            size="large"
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => handlePurchase(subscription)}
                            disabled={!isUserAdmin || (orgfin?.organization.active_users_count > subscription.users || orgfin?.organization.active_project_count > subscription.projects)}
                            sx={{
                              textTransform: 'uppercase',
                              color: theme.palette.common.black,
                              '&:disabled': { // Adding styles for disabled state
                                color: theme.palette.grey[500], // Set text color to gray
                              }
                            }}
                          >
                            Purchase
                          </Button>
                          {orgfin?.organization.active_users_count > subscription.users || orgfin?.organization.active_project_count > subscription.projects ? (
                            <Typography variant="body2" sx={{ color: theme.palette.common.black, marginTop: 2 }}>
                              Please reduce the number of active users or projects before downgrading your subscription.
                            </Typography>
                          ) : null}
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Container>
          )}
        </Box>
        <FloatingButton />
      </div>
    </ThemeProvider>
  );
};

export default SubscriptionPlan;

